import React, {
  useMemo,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import ExcelJS from "exceljs";
import { VariableSizeList as List } from "react-window";
import Export from "../../../../components/ExportBreakdown";
import { Button } from "../../../../nq-component";
import getCustomerCategories from "../customer-count/CustomerCategories";
import LoadingState from "../../../../components/LoadingState";
import getCustomerCategoriesCount from "../customer-count/CustomerCategoriesCount";
import PageHeader from "../../../../components/PageHeader";

function CustomerBreakdownPage({ transactions }) {
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedStore, setSelectedStore] = useState("All");
  const [selectedYear, setSelectedYear] = useState("All");
  const [nameSearch, setNameSearch] = useState("");
  const [numberSearch, setNumberSearch] = useState("");
  const [expandedTransactions, setExpandedTransactions] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [modalCustomer, setModalCustomer] = useState(null);
  const listRef = useRef(null);

  const getUniqueStores = (transactions) => {
    const storeSet = new Set();
    transactions.forEach((transaction) => {
      const storeCode = transaction.store_code?.replace("ANGELSPIZZA_", "");
      storeSet.add(storeCode);
    });
    return Array.from(storeSet);
  };

  const getUniqueYears = (transactions) => {
    const yearSet = new Set();

    transactions?.forEach((customer) => {
      (customer.transactions || []).forEach((tran) => {
        const date = new Date(tran.tran_date || tran.date);
        if (!isNaN(date)) {
          yearSet.add(date.getFullYear());
        }
      });
    });

    return Array.from(yearSet).sort((a, b) => b - a); // descending
  };

  const uniqueYears = useMemo(
    () => getUniqueYears(transactions),
    [transactions],
  );

  const uniqueStores = useMemo(
    () => getUniqueStores(transactions),
    [transactions],
  );

  const filteredCustomers = useMemo(() => {
    const allCustomerCategories = getCustomerCategoriesCount(
      transactions,
      selectedStore,
    );

    // Flatten all categories
    // const flattened = Object.values(allCustomerCategories).flat();
    // Attach RFM category to each customer
    const flattened = Object.entries(allCustomerCategories).flatMap(
      ([category, customers]) =>
        customers.map((customer) => ({
          ...customer,
          rfmCategory: category, // ➕ add this
        })),
    );

    const filtered = flattened.filter((customer) => {
      const matchesName = nameSearch
        ? customer.custName?.toLowerCase().includes(nameSearch.toLowerCase())
        : true;

      const matchesNumber = numberSearch
        ? customer.custMobile?.includes(numberSearch)
        : true;

      const matchesYear =
        selectedYear === "All"
          ? true
          : customer.transactions?.some((t) => {
              const date = new Date(t.date);
              return (
                !isNaN(date) && date.getFullYear().toString() === selectedYear
              );
            });

      // Only filter category if it's not "All"
      const matchesCategory =
        selectedFilter === "All"
          ? true
          : (allCustomerCategories[selectedFilter] || []).some(
              (c) =>
                c.custPhone === customer.custPhone &&
                c.custName === customer.custName,
            );

      return matchesName && matchesNumber && matchesYear && matchesCategory;
    });

    return filtered;
  }, [
    transactions,
    selectedStore,
    selectedFilter,
    nameSearch,
    numberSearch,
    selectedYear,
  ]);

  console.log("filtered", filteredCustomers);

  // const exportCsv = () => {
  //   const header =
  //     "Customer,Email,Mobile Number,Phone Number,Address,Barangay,Birthday,Customer Card,Notes,LandMark,Transaction Count,Total Sales,Last Transaction,Day Last Transaction,RFM Type\n";
  //   const rows = filteredCustomers
  //     .map(
  //       (customer) =>
  //         `"${customer.custName}","${customer.custEmail}","${
  //           customer.custMobile
  //         }","${customer.custPhone}","${customer.address}","${
  //           customer.barangay
  //         }","${customer.birthday}","${customer.card}","${customer.notes}","${
  //           customer.landmark
  //         }","${customer.purchaseCount}","${customer.totalSpend}","${new Date(
  //           customer.lastPurchaseDate,
  //         ).toLocaleDateString("en-US", {
  //           year: "numeric",
  //           month: "long",
  //           day: "numeric",
  //         })}","${customer.daysSinceLastPurchase}","${customer.rfmCategory}"`,
  //     )
  //     .join("\n");

  //   const csv = header + rows;

  //   // Create a Blob from the CSV string
  //   const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

  //   // Create a downloadable link
  //   const url = URL.createObjectURL(blob);
  //   const link = document.createElement("a");
  //   link.setAttribute("href", url);
  //   link.setAttribute("download", "customers_breakdown.csv");

  //   document.body.appendChild(link);
  //   link.click();
  //   document.body.removeChild(link);
  // };

  async function exportCustomerBreakdownXLSX({
    customers = filteredCustomers,
    fileName = "customers_breakdown.xlsx",
    title = "Customer Breakdown",
    storeName = "All Stores",
    dateRangeLabel = "",
  }) {
    if (!Array.isArray(customers) || customers.length === 0) {
      console.warn("exportCustomerBreakdownXLSX: no data");
      return;
    }

    const wb = new ExcelJS.Workbook();
    wb.creator = "Customer Analytics";
    wb.created = new Date();

    const ws = wb.addWorksheet("Customers", {
      views: [{ state: "frozen", ySplit: 5 }],
    });

    // ===== Title =====
    ws.mergeCells("A1:O1");
    ws.getCell("A1").value = title;
    ws.getCell("A1").font = {
      bold: true,
      size: 16,
      color: { argb: "FFFFFFFF" },
    };
    ws.getCell("A1").alignment = { vertical: "middle", horizontal: "left" };
    ws.getCell("A1").fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF1F2937" },
    };
    ws.getRow(1).height = 28;

    // ===== Meta =====
    ws.mergeCells("A2:O2");
    ws.getCell("A2").value =
      `Store: ${storeName}   |   Date Range: ${dateRangeLabel || "N/A"}   |   Generated: ${new Date().toLocaleString()}`;
    ws.getCell("A2").font = {
      italic: true,
      size: 11,
      color: { argb: "FF111827" },
    };
    ws.getCell("A2").alignment = { vertical: "middle", horizontal: "left" };
    ws.getCell("A2").fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF3F4F6" },
    };
    ws.getRow(2).height = 18;

    // Spacer
    ws.addRow([]);
    ws.getRow(3).height = 6;

    // ===== Headers =====
    const headers = [
      "Customer",
      "Email",
      "Mobile Number",
      "Phone Number",
      "Address",
      "Barangay",
      "Birthday",
      "Customer Card",
      "Notes",
      "LandMark",
      "Transaction Count",
      "Total Sales",
      "Last Transaction",
      "Day Last Transaction",
      "RFM Type",
    ];

    ws.addRow(headers);
    const headerRowIndex = 4;
    const headerRow = ws.getRow(headerRowIndex);

    headerRow.height = 18;
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
        wrapText: true,
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF1F4D2C" },
      }; // green header
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // Column sizing
    ws.columns = [
      { width: 26 }, // Customer
      { width: 26 }, // Email
      { width: 16 }, // Mobile
      { width: 16 }, // Phone
      { width: 28 }, // Address
      { width: 18 }, // Barangay
      { width: 14 }, // Birthday
      { width: 16 }, // Customer Card
      { width: 22 }, // Notes
      { width: 18 }, // Landmark
      { width: 18 }, // Tx Count
      { width: 16 }, // Total Sales
      { width: 18 }, // Last Tx
      { width: 22 }, // Days Since Last
      { width: 16 }, // RFM Type
    ];

    const toDateObj = (d) => {
      if (!d) return null;
      const dt = d instanceof Date ? d : new Date(d);
      return isNaN(dt.getTime()) ? null : dt;
    };

    // ===== Data Rows =====
    const firstDataRow = headerRowIndex + 1;

    customers.forEach((c, idx) => {
      const lastTxDate = toDateObj(c.lastPurchaseDate);
      const bday = toDateObj(c.birthday);

      const row = ws.addRow([
        c.custName || "",
        c.custEmail || "",
        c.custMobile || "",
        c.custPhone || "",
        c.address || "",
        c.barangay || "",
        bday || c.birthday || "", // keep raw if not parseable
        c.card || "",
        c.notes || "",
        c.landmark || "",
        Number(c.purchaseCount || 0),
        Number(c.totalSpend || 0),
        lastTxDate || c.lastPurchaseDate || "",
        Number(c.daysSinceLastPurchase ?? 0),
        c.rfmCategory || "",
      ]);

      const isAlt = idx % 2 === 1;

      row.eachCell((cell, col) => {
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };

        // alignment rules
        if ([11, 12, 13, 14].includes(col)) {
          cell.alignment = {
            vertical: "middle",
            horizontal: "center",
            wrapText: true,
          };
        } else {
          cell.alignment = {
            vertical: "middle",
            horizontal: "left",
            wrapText: true,
          };
        }

        // zebra
        if (isAlt) {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFF9FAFB" },
          };
        }

        // formats
        if (col === 12) cell.numFmt = '"₱"#,##0.00'; // Total Sales
        if (col === 11 || col === 14) cell.numFmt = "#,##0"; // counts/days
        if (col === 7 || col === 13) cell.numFmt = "mm/dd/yyyy"; // birthday + last tx
      });
    });

    const lastDataRow = ws.lastRow.number;

    // ===== Auto Filter =====
    ws.autoFilter = {
      from: { row: headerRowIndex, column: 1 },
      to: { row: headerRowIndex, column: headers.length },
    };

    // ===== Optional: Total Row =====
    const totalRowIndex = lastDataRow + 1;
    ws.mergeCells(`A${totalRowIndex}:J${totalRowIndex}`);
    ws.getCell(`A${totalRowIndex}`).value = "TOTAL";
    ws.getCell(`A${totalRowIndex}`).font = {
      bold: true,
      color: { argb: "FFFFFFFF" },
    };
    ws.getCell(`A${totalRowIndex}`).alignment = {
      vertical: "middle",
      horizontal: "right",
    };
    ws.getCell(`A${totalRowIndex}`).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF1F2937" },
    };

    // Sum transaction count (col 11) and total sales (col 12)
    ws.getCell(`K${totalRowIndex}`).value = {
      formula: `SUM(K${firstDataRow}:K${lastDataRow})`,
    };
    ws.getCell(`K${totalRowIndex}`).numFmt = "#,##0";
    ws.getCell(`K${totalRowIndex}`).font = {
      bold: true,
      color: { argb: "FFFFFFFF" },
    };
    ws.getCell(`K${totalRowIndex}`).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF1F2937" },
    };

    ws.getCell(`L${totalRowIndex}`).value = {
      formula: `SUM(L${firstDataRow}:L${lastDataRow})`,
    };
    ws.getCell(`L${totalRowIndex}`).numFmt = '"₱"#,##0.00';
    ws.getCell(`L${totalRowIndex}`).font = {
      bold: true,
      color: { argb: "FFFFFFFF" },
    };
    ws.getCell(`L${totalRowIndex}`).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF1F2937" },
    };

    // ===== Download =====
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => URL.revokeObjectURL(link.href), 500);
  }

  const exportCustomerTransactionsXLSX = async (customer) => {
    if (!customer) return;

    const money = (n) => Number(n || 0);
    const safeDiv = (a, b) =>
      Number(b || 0) ? Number(a || 0) / Number(b || 0) : 0;

    const purchaseCount = Number(
      customer.purchaseCount || customer.transactions?.length || 0,
    );
    const totalSpend = money(customer.totalSpend);
    const avgSales = safeDiv(totalSpend, purchaseCount);

    // Date helpers
    const toDateObj = (d) => {
      const dt = d instanceof Date ? d : new Date(d);
      return isNaN(dt.getTime()) ? null : dt;
    };

    const lastPurchaseDate = toDateObj(customer.lastPurchaseDate);

    // ===== Workbook =====
    const wb = new ExcelJS.Workbook();
    wb.creator = "Customer Analytics";
    wb.created = new Date();

    const ws = wb.addWorksheet("Customer Transactions", {
      views: [{ state: "frozen", ySplit: 5 }],
    });

    // ===== Columns (set later for both tables) =====
    ws.columns = [
      { key: "a", width: 28 },
      { key: "b", width: 20 },
      { key: "c", width: 18 },
      { key: "d", width: 22 },
      { key: "e", width: 34 },
      { key: "f", width: 14 },
      { key: "g", width: 14 },
    ];

    // ===== Title =====
    ws.mergeCells("A1:F1");
    ws.getCell("A1").value = "Customer Transactions Report";
    ws.getCell("A1").font = {
      bold: true,
      size: 16,
      color: { argb: "FFFFFFFF" },
    };
    ws.getCell("A1").alignment = { vertical: "middle", horizontal: "left" };
    ws.getCell("A1").fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF1F2937" }, // dark gray
    };
    ws.getRow(1).height = 28;

    // ===== Subtitle / Meta =====
    ws.mergeCells("A2:F2");
    ws.getCell("A2").value = `Generated: ${new Date().toLocaleString()}`;
    ws.getCell("A2").font = {
      italic: true,
      size: 11,
      color: { argb: "FF374151" },
    };
    ws.getCell("A2").alignment = { vertical: "middle", horizontal: "left" };
    ws.getCell("A2").fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF3F4F6" }, // light gray
    };
    ws.getRow(2).height = 18;

    // Spacer
    ws.addRow([]);
    ws.getRow(3).height = 6;

    // ===== Summary Section =====
    ws.getCell("A4").value = "SUMMARY";
    ws.getCell("A4").font = {
      bold: true,
      size: 12,
      color: { argb: "FF1F4D2C" },
    };

    // Header row for summary table
    const summaryHeaderRow = ws.addRow([
      "Customer Name",
      "Transaction Count",
      "Average Sales",
      "Last Transaction Date",
      "Days Since Last Transaction",
      "RFM Type",
    ]);
    const summaryHeaderIndex = summaryHeaderRow.number;

    // Style summary header
    summaryHeaderRow.height = 18;
    summaryHeaderRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
        wrapText: true,
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF1F4D2C" },
      }; // brand green
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // Summary data row
    const summaryDataRow = ws.addRow([
      customer.custName || "-",
      purchaseCount,
      avgSales,
      lastPurchaseDate || "",
      Number(customer.daysSinceLastPurchase ?? ""),
      (customer.rfmCategory || "-").toString(),
    ]);

    // Style summary data
    summaryDataRow.eachCell((cell, col) => {
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
      cell.alignment = {
        vertical: "middle",
        horizontal: col === 1 ? "left" : "center",
      };

      // formats
      if (col === 3) cell.numFmt = '"₱"#,##0.00';
      if (col === 4) cell.numFmt = "mm/dd/yyyy";
      if (col === 2 || col === 5) cell.numFmt = "#,##0";
    });

    // Spacer between tables
    ws.addRow([]);
    ws.getRow(ws.lastRow.number).height = 10;

    // ===== Transactions Section Title =====
    const txTitleRow = ws.addRow(["TRANSACTIONS"]);
    ws.mergeCells(`A${txTitleRow.number}:F${txTitleRow.number}`);
    ws.getCell(`A${txTitleRow.number}`).font = {
      bold: true,
      size: 12,
      color: { argb: "FF111827" },
    };
    ws.getCell(`A${txTitleRow.number}`).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF9FAFB" },
    };
    ws.getRow(txTitleRow.number).height = 18;

    // Transactions table header
    const txHeaderRow = ws.addRow(["Transaction Date", "Spend Total", "Store"]);
    const txHeaderIndex = txHeaderRow.number;

    // Style tx header
    txHeaderRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.alignment = { vertical: "middle", horizontal: "center" };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF2563EB" },
      }; // blue header
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // Set widths for tx columns (A-C used here)
    ws.getColumn(1).width = 22; // date
    ws.getColumn(2).width = 16; // spend
    ws.getColumn(3).width = 26; // store

    // Add tx rows
    const txs = Array.isArray(customer.transactions)
      ? customer.transactions
      : [];
    txs.forEach((t, idx) => {
      const dt = toDateObj(t.date) || t.date; // keep raw if not parseable
      const row = ws.addRow([
        dt || "",
        money(t.totalSpend),
        t.storeSpend || "",
      ]);

      // zebra style
      const isAlt = idx % 2 === 1;
      row.eachCell((cell, col) => {
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
        cell.alignment = {
          vertical: "middle",
          horizontal: col === 3 ? "left" : "center",
        };

        if (col === 1) cell.numFmt = "mm/dd/yyyy";
        if (col === 2) cell.numFmt = '"₱"#,##0.00';

        if (isAlt) {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFF9FAFB" },
          };
        }
      });
    });

    // Auto filter on transactions header
    ws.autoFilter = {
      from: { row: txHeaderIndex, column: 1 },
      to: { row: txHeaderIndex, column: 3 },
    };

    // ===== Download =====
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const safeName = (customer.custName || "customer")
      .replace(/[\\/:*?"<>|]/g, "")
      .replace(/\s+/g, "_");

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${safeName}_transactions.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => URL.revokeObjectURL(link.href), 500);
  };

  const getItemSize = useCallback(
    (index) => {
      const base = 70;
      const isExpanded = selectedItem === index;
      const customer = filteredCustomers[index];
      const showTransactions = expandedTransactions[index];
      const txCount = showTransactions
        ? customer?.transactions?.length || 0
        : 0;

      const cardHeight = 120; // estimated height per transaction card
      const expandedContent = isExpanded ? 150 : 0; // base for expanded summary/buttons

      return base + expandedContent + txCount * cardHeight;
    },
    [selectedItem, expandedTransactions, filteredCustomers],
  );

  useEffect(() => {
    if (listRef.current) {
      listRef.current.resetAfterIndex(0, true);
    }
  }, [selectedItem, expandedTransactions]);

  const Row = ({ index, style }) => {
    const customer = filteredCustomers[index];

    console.log("cust", customer);

    const isExpanded = selectedItem === index;

    return (
      <div
        style={{
          ...style,
          borderBottom: "1px solid #ccc",
          padding: "10px",
          backgroundColor: "#f9f9f9",
        }}
      >
        <div
          style={{
            display: "flex",
            padding: "5px",
            cursor: "pointer",
            fontWeight: "bold",
            alignItems: "center",
          }}
          onClick={() => setSelectedItem(isExpanded ? null : index)}
        >
          <div style={{ flex: 1, fontSize: "10px" }}>{index + 1}</div>
          <div style={{ flex: 2, fontSize: "10px" }}>{customer?.custName}</div>
          <div className="text-center" style={{ flex: 2, fontSize: "10px" }}>
            {customer?.custEmail || "-"}
          </div>
          <div className="text-center" style={{ flex: 2, fontSize: "10px" }}>
            {customer?.custMobile || "-"}
          </div>
          <div className="text-center" style={{ flex: 2, fontSize: "10px" }}>
            {customer?.custPhone || "-"}
          </div>

          <div className="text-center" style={{ flex: 2, fontSize: "10px" }}>
            {/* loyalist, potentialLoyalists etc */}
            <div
              className="text-center"
              style={{ flex: 2, fontSize: "10px", textTransform: "capitalize" }}
            >
              {customer?.rfmCategory || "-"}
            </div>
          </div>
        </div>

        {isExpanded && (
          <div className="mt-3 ps-3">
            <ul className="mb-2 ps-3">
              <li>
                <strong>Transaction Count:</strong>{" "}
                {customer.transactions?.length}
              </li>
              <li>
                <strong>Total Sales:</strong> ₱
                {customer?.totalSpend.toLocaleString("en-PH", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </li>

              <li>
                <strong>Last Transaction:</strong>{" "}
                {new Date(customer.lastPurchaseDate).toLocaleDateString(
                  "en-US",
                  {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  },
                )}
              </li>

              <li>
                <strong>Days Since Last Transaction:</strong>{" "}
                {customer.daysSinceLastPurchase}
              </li>
            </ul>

            <div className="row g-2 mt-3">
              <div className="col-12 col-md-6 col-lg-auto">
                <button
                  className="btn w-100"
                  style={{
                    borderRadius: 12,
                    fontWeight: 900,
                    padding: "10px 14px",
                    background:
                      "linear-gradient(135deg, #1F4D2C 0%, #83c14a 100%)",
                    color: "#fff",
                    border: "none",
                    boxShadow: "0 10px 22px rgba(0,0,0,0.10)",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setModalCustomer(customer);
                    setShowModal(true);
                  }}
                >
                  <i className="bi bi-eye me-2" />
                  View Customer Transactions
                </button>
              </div>

              <div className="col-12 col-md-6 col-lg-auto">
                <button
                  className="btn w-100"
                  style={{
                    borderRadius: 12,
                    fontWeight: 900,
                    padding: "10px 14px",
                    border: "1px solid rgba(31,77,44,0.25)",
                    background: "rgba(31,77,44,0.08)",
                    color: "#1F4D2C",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    exportCustomerTransactionsXLSX(customer);
                  }}
                >
                  <i className="bi bi-download me-2" />
                  Export Transactions
                </button>
              </div>
            </div>

            {/* {expandedTransactions[index] &&
              customer.transactions?.map((t, i) => (
                <div className="card p-2 mb-2" key={i}>
                  <p>
                    <strong>Store:</strong> {t.storeSpend}
                  </p>
                  <p>
                    <strong>Date:</strong> {t.date.substring(0, 10)}
                  </p>
                  <p>
                    <strong>Transaction ID:</strong> {t.tranId}
                  </p>
                  <p>
                    <strong>Spend:</strong> ₱{t.totalSpend.toFixed(2)}
                  </p>
                </div>
              ))} */}
          </div>
        )}
      </div>
    );
  };

  console.log("fill", filteredCustomers);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-1 mt-4">
        <PageHeader
          title="Customer Breakdown"
          subtitle="Customer segmentation across purchase behavior"
          icon="bi bi-pie-chart-fill"
        />

        <Export exportCsv={exportCustomerBreakdownXLSX} />
      </div>

      {/* Filters */}
      {/* <div className="row mb-3">
        <div className="col-sm-3">
          <select
            className="form-select"
            onChange={(e) => setSelectedStore(e.target.value)}
          >
            <option value="All">All Stores</option>
            {uniqueStores.map((s, i) => (
              <option key={i} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div className="col-sm-3">
          <select
            className="form-select"
            onChange={(e) => setSelectedFilter(e.target.value)}
          >
            <option value="All">All</option>
            <option value="loyalists">Loyalist</option>
            <option value="potentialLoyalists">Potential Loyalist</option>
            <option value="promising">PROMISING</option>
            <option value="hesitant">HESITANT</option>
            <option value="needAttention">NEED ATTENTION</option>
            <option value="detractors">DETRACTORS</option>
          </select>
        </div>
        <div className="col-sm-3">
          <input
            className="form-control"
            placeholder="Search Name"
            onChange={(e) => setNameSearch(e.target.value)}
          />
        </div>
        <div className="col-sm-3">
          <input
            className="form-control"
            placeholder="Search Mobile Number"
            onChange={(e) => setNumberSearch(e.target.value)}
          />
        </div>

        <div className="col-sm-3 mt-2">
          <select
            className="form-select"
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            <option value="All">All Years</option>
            {uniqueYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div> */}

      {/* Filters */}
      <div
        className="row g-2 align-items-end mb-3"
        style={{
          background: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: 16,
          padding: 14,
          boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
        }}
      >
        {/* Store */}
        <div className="col-12 col-md-6 col-lg-3">
          <label
            style={{
              fontSize: "0.75rem",
              fontWeight: 700,
              color: "#6b7280",
              marginBottom: 6,
              display: "block",
              letterSpacing: 0.4,
            }}
          >
            STORE
          </label>
          <div style={{ position: "relative" }}>
            <i
              className="bi bi-shop"
              style={{
                position: "absolute",
                top: "50%",
                left: 12,
                transform: "translateY(-50%)",
                color: "#9ca3af",
                pointerEvents: "none",
              }}
            />
            <select
              className="form-select"
              onChange={(e) => setSelectedStore(e.target.value)}
              style={{
                height: 44,
                paddingLeft: 38,
                borderRadius: 12,
                border: "1px solid #e5e7eb",
                fontWeight: 600,
                boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
              }}
            >
              <option value="All">All Stores</option>
              {uniqueStores.map((s, i) => (
                <option key={i} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Segment */}
        <div className="col-12 col-md-6 col-lg-3">
          <label
            style={{
              fontSize: "0.75rem",
              fontWeight: 700,
              color: "#6b7280",
              marginBottom: 6,
              display: "block",
              letterSpacing: 0.4,
            }}
          >
            SEGMENT
          </label>
          <div style={{ position: "relative" }}>
            <i
              className="bi bi-people"
              style={{
                position: "absolute",
                top: "50%",
                left: 12,
                transform: "translateY(-50%)",
                color: "#9ca3af",
                pointerEvents: "none",
              }}
            />
            <select
              className="form-select"
              onChange={(e) => setSelectedFilter(e.target.value)}
              style={{
                height: 44,
                paddingLeft: 38,
                borderRadius: 12,
                border: "1px solid #e5e7eb",
                fontWeight: 600,
                boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
              }}
            >
              <option value="All">All</option>
              <option value="loyalists">Loyalist</option>
              <option value="potentialLoyalists">Potential Loyalist</option>
              <option value="promising">Promising</option>
              <option value="hesitant">Hesitant</option>
              <option value="needAttention">Need Attention</option>
              <option value="detractors">Detractors</option>
            </select>
          </div>
        </div>

        {/* Search Name */}
        <div className="col-12 col-md-6 col-lg-3">
          <label
            style={{
              fontSize: "0.75rem",
              fontWeight: 700,
              color: "#6b7280",
              marginBottom: 6,
              display: "block",
              letterSpacing: 0.4,
            }}
          >
            SEARCH NAME
          </label>
          <div style={{ position: "relative" }}>
            <i
              className="bi bi-search"
              style={{
                position: "absolute",
                top: "50%",
                left: 12,
                transform: "translateY(-50%)",
                color: "#9ca3af",
                pointerEvents: "none",
              }}
            />
            <input
              className="form-control"
              placeholder="Type customer name..."
              onChange={(e) => setNameSearch(e.target.value)}
              style={{
                height: 44,
                paddingLeft: 38,
                borderRadius: 12,
                border: "1px solid #e5e7eb",
                fontWeight: 600,
                boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
              }}
            />
          </div>
        </div>

        {/* Search Mobile */}
        <div className="col-12 col-md-6 col-lg-3">
          <label
            style={{
              fontSize: "0.75rem",
              fontWeight: 700,
              color: "#6b7280",
              marginBottom: 6,
              display: "block",
              letterSpacing: 0.4,
            }}
          >
            MOBILE NUMBER
          </label>
          <div style={{ position: "relative" }}>
            <i
              className="bi bi-telephone"
              style={{
                position: "absolute",
                top: "50%",
                left: 12,
                transform: "translateY(-50%)",
                color: "#9ca3af",
                pointerEvents: "none",
              }}
            />
            <input
              className="form-control"
              placeholder="Search mobile..."
              onChange={(e) => setNumberSearch(e.target.value)}
              style={{
                height: 44,
                paddingLeft: 38,
                borderRadius: 12,
                border: "1px solid #e5e7eb",
                fontWeight: 600,
                boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
              }}
            />
          </div>
        </div>

        {/* Year */}
        <div className="col-12 col-md-6 col-lg-3">
          <label
            style={{
              fontSize: "0.75rem",
              fontWeight: 700,
              color: "#6b7280",
              marginBottom: 6,
              display: "block",
              letterSpacing: 0.4,
            }}
          >
            YEAR
          </label>
          <div style={{ position: "relative" }}>
            <i
              className="bi bi-calendar3"
              style={{
                position: "absolute",
                top: "50%",
                left: 12,
                transform: "translateY(-50%)",
                color: "#9ca3af",
                pointerEvents: "none",
              }}
            />
            <select
              className="form-select"
              onChange={(e) => setSelectedYear(e.target.value)}
              style={{
                height: 44,
                paddingLeft: 38,
                borderRadius: 12,
                border: "1px solid #e5e7eb",
                fontWeight: 600,
                boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
              }}
            >
              <option value="All">All Years</option>
              {uniqueYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <div className="text-end">
        <p>
          <strong>Total:</strong> {filteredCustomers.length}
        </p>
      </div>

      <div
        style={{
          display: "flex",
          backgroundColor: "#83c14a",
          color: "white",
          fontWeight: "bold",
          padding: "10px",
        }}
      >
        <div style={{ flex: 1 }}>#</div>
        <div style={{ flex: 2 }}>Customer</div>
        <div style={{ flex: 2 }}>Email</div>
        <div style={{ flex: 2 }}>Mobile</div>
        <div style={{ flex: 2 }}>Phone</div>
        <div style={{ flex: 1 }}>RFM</div>
      </div>

      {/* Virtualized List */}
      <List
        ref={listRef}
        height={600}
        itemCount={filteredCustomers.length}
        itemSize={getItemSize}
        width="100%"
      >
        {Row}
      </List>

      {/* {showModal && modalCustomer && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0,0,0,0.5)",
          }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Transactions for {modalCustomer.cust_name}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                {modalCustomer?.transactions?.length > 0 ? (
                  modalCustomer.transactions.map((t, i) => (
                    <div className="card p-3 mb-3" key={i}>
                      <p>
                        <strong>Store:</strong> {t.storeSpend}
                      </p>

                      <p>
                        <strong>Date:</strong>{" "}
                        {new Date(t.date).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </p>

                      <p>
                        <strong>Transaction ID:</strong> {t.tranId}
                      </p>
                      <p>
                        <strong>Spend:</strong> ₱
                        {Number(t.totalSpend).toLocaleString("en-PH", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </p>
                    </div>
                  ))
                ) : (
                  <LoadingState />
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )} */}
      {showModal && modalCustomer && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0,0,0,0.55)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
          }}
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div
              className="modal-content border-0"
              style={{
                borderRadius: 18,
                overflow: "hidden",
                boxShadow: "0 30px 80px rgba(0,0,0,0.25)",
              }}
            >
              {/* Header */}
              <div
                className="modal-header"
                style={{
                  borderBottom: "1px solid #f3f4f6",
                  padding: "16px 18px",
                  background:
                    "linear-gradient(135deg, rgba(31,77,44,1) 0%, rgba(131,193,74,1) 100%)",
                  color: "#fff",
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                }}
              >
                <div className="d-flex align-items-center gap-3">
                  {/* Avatar circle */}
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 999,
                      backgroundColor: "rgba(255,255,255,0.18)",
                      border: "1px solid rgba(255,255,255,0.25)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 900,
                      fontSize: 16,
                    }}
                    title={modalCustomer.cust_name}
                  >
                    {(modalCustomer.cust_name || "C")
                      .trim()
                      .split(" ")
                      .slice(0, 2)
                      .map((w) => w[0]?.toUpperCase())
                      .join("")}
                  </div>

                  <div>
                    <div
                      style={{
                        fontWeight: 900,
                        fontSize: 16,
                        lineHeight: 1.15,
                      }}
                    >
                      Transactions
                    </div>
                    <div style={{ opacity: 0.9, fontSize: 13 }}>
                      {modalCustomer.cust_name}
                      {modalCustomer?.transactions?.length
                        ? ` • ${modalCustomer.transactions.length} records`
                        : ""}
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  className="btn"
                  onClick={() => setShowModal(false)}
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 12,
                    backgroundColor: "rgba(255,255,255,0.18)",
                    border: "1px solid rgba(255,255,255,0.25)",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  aria-label="Close"
                >
                  <i className="bi bi-x-lg" />
                </button>
              </div>

              {/* Body */}
              <div
                className="modal-body"
                style={{ padding: 16, background: "#f9fafb" }}
              >
                {modalCustomer?.transactions?.length > 0 ? (
                  modalCustomer.transactions.map((t, i) => {
                    const dateLabel = t?.date
                      ? new Date(t.date).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })
                      : "N/A";

                    const spendLabel = Number(
                      t?.totalSpend || 0,
                    ).toLocaleString("en-PH", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    });

                    return (
                      <div
                        key={i}
                        className="mb-3"
                        style={{
                          borderRadius: 16,
                          background: "#ffffff",
                          border: "1px solid #e5e7eb",
                          boxShadow: "0 10px 22px rgba(0,0,0,0.06)",
                          overflow: "hidden",
                          transition: "transform 0.12s ease",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.transform = "translateY(-2px)")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.transform = "translateY(0)")
                        }
                      >
                        {/* Card top stripe */}
                        <div
                          style={{
                            height: 6,
                            background:
                              "linear-gradient(90deg, rgba(31,77,44,1), rgba(131,193,74,1))",
                          }}
                        />

                        <div style={{ padding: 14 }}>
                          {/* Row 1: Store + Spend chip */}
                          <div className="d-flex align-items-center justify-content-between gap-2">
                            <div className="d-flex align-items-center gap-2">
                              <span
                                style={{
                                  width: 34,
                                  height: 34,
                                  borderRadius: 12,
                                  background: "rgba(31,77,44,0.10)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "#1F4D2C",
                                }}
                              >
                                <i className="bi bi-shop" />
                              </span>
                              <div>
                                <div
                                  style={{ fontWeight: 900, color: "#111827" }}
                                >
                                  {t.storeSpend || "Store"}
                                </div>
                                <div style={{ fontSize: 12, color: "#6b7280" }}>
                                  <i className="bi bi-calendar3 me-1" />
                                  {dateLabel}
                                </div>
                              </div>
                            </div>

                            <span
                              className="badge"
                              style={{
                                padding: "10px 12px",
                                borderRadius: 999,
                                background: "rgba(31,77,44,0.10)",
                                border: "1px solid rgba(31,77,44,0.18)",
                                color: "#1F4D2C",
                                fontWeight: 900,
                                fontSize: 12,
                                whiteSpace: "nowrap",
                              }}
                              title="Total Spend"
                            >
                              ₱ {spendLabel}
                            </span>
                          </div>

                          {/* Divider */}
                          <div
                            style={{
                              height: 1,
                              background: "#f3f4f6",
                              margin: "12px 0",
                            }}
                          />

                          {/* Row 2: Transaction ID */}
                          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                            <div style={{ fontSize: 12, color: "#6b7280" }}>
                              <i className="bi bi-receipt me-2" />
                              Transaction ID
                            </div>

                            <span
                              style={{
                                fontFamily:
                                  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                fontSize: 12,
                                fontWeight: 800,
                                color: "#111827",
                                background: "#f3f4f6",
                                border: "1px solid #e5e7eb",
                                padding: "6px 10px",
                                borderRadius: 10,
                              }}
                            >
                              {t.tranId || "N/A"}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <LoadingState />
                )}
              </div>

              {/* Footer */}
              <div
                className="modal-footer"
                style={{
                  borderTop: "1px solid #f3f4f6",
                  padding: "12px 16px",
                  background: "#ffffff",
                  position: "sticky",
                  bottom: 0,
                  zIndex: 2,
                }}
              >
                <button
                  className="btn"
                  onClick={() => setShowModal(false)}
                  style={{
                    borderRadius: 12,
                    fontWeight: 900,
                    padding: "10px 14px",
                    border: "1px solid rgba(31,77,44,0.25)",
                    background: "rgba(31,77,44,0.08)",
                    color: "#1F4D2C",
                  }}
                >
                  <i className="bi bi-x-circle me-2" />
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CustomerBreakdownPage;
